import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Dumbbell,
  Heart,
  Flame,
  Activity,
  TrendingUp,
  Clock,
  Play,
  Info,
  CheckCircle2,
  CalendarDays,
  Repeat,
  MoreVertical,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import ExerciseDetailsModal from "@/components/dashboard/ExerciseDetailsModal";
import SwapWorkoutDialog from "./SwapWorkoutDialog";
import SwapExerciseDialog from "./SwapExerciseDialog";

/**
 * Premium Weekly Workout Schedule
 *
 * Design goals (taken straight from the user's mock):
 *   - Header row with bold "This Week" + ◀ Jun 23 – Jun 29 ▶ arrows for
 *     paginating through past weeks. Future-week navigation is disabled
 *     because we don't pre-schedule beyond the current week.
 *   - Horizontal Mon–Sun pill row with today highlighted (filled sky
 *     circle), Sat/Sun in a warmer accent.
 *   - Color-coded day accordion rows. Color is derived from the workout's
 *     `goal` field via `categoryFor()` below. Today's row is auto-expanded
 *     with a glowing sky border.
 *   - When expanded, each row shows the exercises with thumbnails (pulled
 *     from the global Exercise catalogue), sets×reps, and an info icon
 *     that opens the existing `ExerciseDetailsModal`.
 *   - 3-dot menus on the workout card and on each exercise let the user
 *     swap them — opens dedicated picker dialogs.
 *   - Historical weeks (any range that does NOT contain today) read from
 *     completed `WorkoutSession`s only. The user can scroll back in
 *     history with the arrows.
 *
 * NOTE: the parent `WorkoutsPage` already wraps this view inside a
 * MembershipGate, so we can assume Strength+ tier.
 */

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_SHORT = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// ---------------------------------------------------------------------------
// Date helpers — pure, no Date mutation
// ---------------------------------------------------------------------------

function startOfWeek(date) {
  // Monday-first week. We clone the date and step back to the Monday at 00:00.
  const d = new Date(date);
  const day = d.getDay(); // 0=Sunday..6=Saturday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(d) {
  // YYYY-MM-DD — matches `WorkoutSession.completed_date` format.
  return new Date(d).toISOString().slice(0, 10);
}

function fmtRange(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const fmt = (d, opts) => d.toLocaleString("en-US", opts);
  if (sameMonth) {
    return `${fmt(monday, { month: "short", day: "numeric" })} – ${fmt(sunday, { day: "numeric" })}`;
  }
  return `${fmt(monday, { month: "short", day: "numeric" })} – ${fmt(sunday, { month: "short", day: "numeric" })}`;
}

// ---------------------------------------------------------------------------
// Category derivation — maps a workout's `goal` to a colour + icon. Matches
// the screenshot: Blue=Strength, Purple=Upper Body / Hypertrophy,
// Orange=Conditioning, Green=Mobility/Recovery.
// ---------------------------------------------------------------------------

function categoryFor(plan) {
  const goal = (plan?.goal || "").toLowerCase();
  const name = (plan?.name || "").toLowerCase();
  if (/recovery|mobility|stretch|yoga/.test(name) || goal === "endurance" && /mobil|recover/.test(name)) {
    return { key: "mobility", label: "Mobility", tone: "text-emerald-300", chipBg: "bg-emerald-500/15", chipBorder: "border-emerald-400/30", accent: "bg-emerald-400", icon: Heart };
  }
  if (/cardio|hiit|conditioning|sprint|run/.test(name) || goal === "fat_loss" || goal === "endurance") {
    return { key: "conditioning", label: "Conditioning", tone: "text-orange-300", chipBg: "bg-orange-500/15", chipBorder: "border-orange-400/30", accent: "bg-orange-400", icon: Flame };
  }
  if (goal === "hypertrophy" || /upper|push|pull|chest|back|arms|shoulder/.test(name)) {
    return { key: "upper_body", label: "Hypertrophy", tone: "text-violet-300", chipBg: "bg-violet-500/15", chipBorder: "border-violet-400/30", accent: "bg-violet-400", icon: Activity };
  }
  // Default → Strength (blue)
  return { key: "strength", label: "Strength", tone: "text-sky-300", chipBg: "bg-sky-500/15", chipBorder: "border-sky-400/30", accent: "bg-sky-400", icon: Dumbbell };
}

const DIFFICULTY_TONE = {
  beginner: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  intermediate: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  advanced: "bg-rose-500/15 text-rose-300 border-rose-400/30",
};

function estimateDurationMin(exercises = []) {
  if (!exercises.length) return 0;
  let total = 0;
  for (const ex of exercises) {
    const sets = Number(ex.sets) || 3;
    const rest = Number(ex.rest_seconds) || 60;
    total += sets * 45 + sets * rest;
  }
  return Math.round(total / 60);
}

// ---------------------------------------------------------------------------
// ExerciseRow — thumbnail + name + sets×reps + info icon + ⋮ swap menu
// ---------------------------------------------------------------------------

function ExerciseRow({ ex, idx, fullExercise, accent, onSwap }) {
  const [modalOpen, setModalOpen] = useState(false);
  const gif = ex.gifUrl || ex.gif_url || fullExercise?.gifUrl || fullExercise?.gif_url || fullExercise?.video_url || null;
  return (
    <li
      data-testid={`weekly-exercise-${idx}`}
      className="flex items-center gap-3 py-2.5 border-b border-white/[0.06] last:border-b-0"
    >
      <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-black/40 flex items-center justify-center">
        {gif ? (
          <img src={gif} alt={ex.name} className="w-full h-full object-cover" />
        ) : (
          <Dumbbell className="w-5 h-5 text-white/30" />
        )}
      </div>
      <span className={`shrink-0 font-display font-bold text-base ${accent}`}>{idx + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold capitalize truncate">{ex.name}</p>
        <p className="text-xs text-white/55">
          {(ex.sets || 3)} sets × {ex.reps || "—"} reps
        </p>
      </div>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        aria-label="Exercise details"
        data-testid={`weekly-exercise-info-${idx}`}
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition"
      >
        <Info className="w-4 h-4" />
      </button>
      {onSwap && (
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid={`weekly-exercise-menu-${idx}`}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition"
            aria-label="Exercise options"
          >
            <MoreVertical className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-[#0B1220] border-white/15 text-white"
          >
            <DropdownMenuItem
              onClick={() => onSwap(idx)}
              data-testid={`weekly-exercise-swap-${idx}`}
              className="cursor-pointer"
            >
              <Repeat className="w-4 h-4 mr-2" /> Swap exercise
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {modalOpen && (
        <ExerciseDetailsModal
          exercise={{
            ...fullExercise,
            ...ex,
            gifUrl: gif,
            instructions: fullExercise?.instructions || ex.instructions,
          }}
          open={modalOpen}
          onOpenChange={setModalOpen}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// DayCard — single accordion row in the weekly list
// ---------------------------------------------------------------------------

function DayCard({
  dayKey,
  dayIdx,
  plan,
  isToday,
  isExpanded,
  onToggle,
  exerciseByName,
  onStartWorkout,
  onSwapWorkout,
  onSwapExercise,
  completedSession,
  isReadOnly,
}) {
  const navigate = useNavigate();
  const category = categoryFor(plan || {});
  const Icon = category.icon;
  const exCount = (plan?.exercises || []).length;
  const durationMin = estimateDurationMin(plan?.exercises || []);
  const dayName = DAY_LONG[dayIdx];

  if (!plan) {
    // Rest day or unscheduled — quieter row. Tappable on the current week
    // so the user can drop a workout onto an empty slot (re-uses the
    // existing Swap dialog with no current plan selected).
    const interactive = !isReadOnly && !!onSwapWorkout;
    return (
      <button
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onSwapWorkout(dayKey)}
        data-testid={`weekly-day-card-${dayKey}`}
        className={`group relative pl-4 pr-3 py-3 rounded-xl border border-white/[0.06] bg-white/[0.02] w-full flex items-center gap-3 text-left transition ${
          interactive ? "hover:border-sky-400/40 hover:bg-white/[0.05]" : "cursor-default"
        }`}
      >
        <span className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-white/15" />
        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/35 group-hover:text-sky-300 group-hover:border-sky-400/30 transition">
          {interactive ? <Plus className="w-4 h-4" /> : <CalendarDays className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-widest text-white/40">{dayName.toUpperCase()}</p>
          <p className="text-sm text-white/55 group-hover:text-white/80 transition">
            {interactive ? "Tap to add a workout" : "Rest day"}
          </p>
        </div>
      </button>
    );
  }

  return (
    <div
      data-testid={`weekly-day-card-${dayKey}`}
      className={`relative rounded-2xl border transition overflow-hidden ${
        isToday
          ? "border-sky-400/60 bg-gradient-to-b from-sky-500/[0.06] to-transparent shadow-[0_0_0_1px_rgba(56,189,248,0.25),0_0_28px_-8px_rgba(56,189,248,0.55)]"
          : "border-white/10 bg-white/[0.03]"
      }`}
    >
      {/* Left coloured rail by category */}
      <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${category.accent}`} />

      <button
        type="button"
        onClick={onToggle}
        data-testid={`weekly-day-toggle-${dayKey}`}
        className="w-full pl-4 pr-3 py-3 flex items-center gap-3 text-left"
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${category.chipBorder} ${category.chipBg}`}>
          <Icon className={`w-5 h-5 ${category.tone}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold tracking-widest ${category.tone}`}>
            {dayName.toUpperCase()}
          </p>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <p className="text-[15px] font-bold truncate">{plan.name}</p>
            <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${category.chipBg} ${category.chipBorder} ${category.tone}`}>
              {category.label}
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {!isExpanded && (
            <p className="text-xs text-white/55">
              {exCount} exercise{exCount === 1 ? "" : "s"}
            </p>
          )}
          {completedSession && (
            <p className="text-[10px] text-emerald-300 inline-flex items-center gap-0.5 mt-0.5">
              <CheckCircle2 className="w-3 h-3" /> Completed
            </p>
          )}
        </div>
        <ChevronDown
          className={`shrink-0 w-5 h-5 text-white/40 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 -mt-1 space-y-3" data-testid={`weekly-day-expanded-${dayKey}`}>
          {/* Meta row inside the expanded card */}
          <div className="flex items-center gap-2 flex-wrap">
            {plan.difficulty && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${DIFFICULTY_TONE[plan.difficulty] || ""} capitalize`}>
                {plan.difficulty}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-white/60">
              <Dumbbell className="w-3.5 h-3.5" /> {exCount} exercises
            </span>
            {durationMin > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-white/60">
                <Clock className="w-3.5 h-3.5" /> {durationMin} min
              </span>
            )}
            <div className="flex-1" />
            {!isReadOnly && (
              <>
                {isToday && (
                  <Button
                    size="sm"
                    onClick={() => onStartWorkout(plan)}
                    data-testid={`weekly-start-workout-${dayKey}`}
                    className="bg-sky-500 hover:bg-sky-400 text-white shadow-[0_4px_14px_-4px_rgba(56,189,248,0.6)]"
                  >
                    <Play className="w-4 h-4 mr-1.5" /> Start Workout
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid={`weekly-workout-menu-${dayKey}`}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition"
                    aria-label="Workout options"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-[#0B1220] border-white/15 text-white"
                  >
                    <DropdownMenuItem
                      onClick={() => onSwapWorkout(dayKey)}
                      data-testid={`weekly-workout-swap-${dayKey}`}
                      className="cursor-pointer"
                    >
                      <Repeat className="w-4 h-4 mr-2" /> Swap workout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {/* Exercise list */}
          {exCount === 0 ? (
            <p className="text-sm text-white/55">No exercises in this workout yet.</p>
          ) : (
            <ul className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-3">
              {plan.exercises.map((ex, i) => (
                <ExerciseRow
                  key={`${ex.name}-${i}`}
                  ex={ex}
                  idx={i}
                  fullExercise={exerciseByName.get((ex.name || "").toLowerCase())}
                  accent={category.tone}
                  onSwap={isReadOnly ? null : () => onSwapExercise({ plan, exerciseIdx: i })}
                />
              ))}
            </ul>
          )}

          {/* Bottom link to the existing per-day dialog (for legacy actions
              like reassigning a different plan to this day). */}
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => navigate(`/workouts`)}
              className="w-full text-right text-xs text-sky-400 inline-flex items-center justify-end gap-1 hover:text-sky-300"
            >
              View Exercise Details <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function WeeklyWorkoutPlanView({ userId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The Monday of the week currently being viewed. Defaults to the Monday
  // of TODAY so the first paint always shows the active week.
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentWeekStart = startOfWeek(today);
  const isCurrentWeek = weekStart.getTime() === currentWeekStart.getTime();
  const isFutureWeek = weekStart.getTime() > currentWeekStart.getTime();
  const isReadOnly = !isCurrentWeek; // can't Start/Swap on past weeks

  // Track which day is expanded (string day-key). Today auto-expands when
  // viewing the current week; otherwise nothing is open by default.
  const todayKey = DAY_KEYS[(today.getDay() + 6) % 7];
  const [expandedDay, setExpandedDay] = useState(isCurrentWeek ? todayKey : null);

  useEffect(() => {
    setExpandedDay(isCurrentWeek ? todayKey : null);
  }, [isCurrentWeek, todayKey, weekStart]);

  // --- Data: the user's *current* weekly plan (always — used when isCurrentWeek)
  const { data: weeklyWorkoutPlan = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", userId],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: userId }).then(
        (r) => r[0] || null,
      ),
    enabled: !!userId,
  });

  // All of the user's WorkoutPlans — used to dereference plan ids.
  const { data: workouts = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list("-created_date"),
  });
  const workoutsById = useMemo(() => {
    const m = new Map();
    for (const w of workouts) m.set(w.id, w);
    return m;
  }, [workouts]);

  // Global Exercise catalogue — for gif thumbnails inside expanded rows.
  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
  });
  const exerciseByName = useMemo(() => {
    const m = new Map();
    for (const e of allExercises) {
      if (e?.name) m.set(e.name.trim().toLowerCase(), e);
    }
    return m;
  }, [allExercises]);

  // Completed WorkoutSessions — used both to mark "Completed" on the
  // current week AND to populate past weeks.
  const { data: sessions = [] } = useQuery({
    queryKey: ["workoutSessions", userId],
    queryFn: () => base44.entities.WorkoutSession.list("-completed_date"),
    enabled: !!userId,
  });
  const sessionsByDate = useMemo(() => {
    const m = new Map();
    for (const s of sessions) {
      const d = s.completed_date || (s.created_date || "").slice(0, 10);
      if (!d) continue;
      // Keep the latest one if a day has multiple sessions.
      if (!m.has(d)) m.set(d, s);
    }
    return m;
  }, [sessions]);

  // ---- Resolve the week into 7 entries (dayKey → { plan, completedSession })
  const weekDays = useMemo(() => {
    return DAY_KEYS.map((dk, idx) => {
      const date = addDays(weekStart, idx);
      const dateISO = isoDate(date);
      const completedSession = sessionsByDate.get(dateISO) || null;
      let plan = null;
      if (isCurrentWeek) {
        const pid = weeklyWorkoutPlan?.[dk];
        plan = pid ? workoutsById.get(pid) || null : null;
      } else if (completedSession) {
        // Past week — show the historical plan if we still have it on file.
        plan = workoutsById.get(completedSession.workout_plan_id) || {
          // Synthesise a stub so the row still renders even if the plan
          // has since been deleted.
          id: completedSession.workout_plan_id,
          name: completedSession.plan_name || "Workout",
          exercises: [],
        };
      }
      return { dayKey: dk, dayIdx: idx, date, dateISO, plan, completedSession };
    });
  }, [weekStart, isCurrentWeek, weeklyWorkoutPlan, workoutsById, sessionsByDate]);

  // ---- Swap state
  const [swapWorkoutDayKey, setSwapWorkoutDayKey] = useState(null);
  const [swapExerciseCtx, setSwapExerciseCtx] = useState(null); // {plan, exerciseIdx}

  const handleStartWorkout = (plan) => {
    if (!plan?.id) return;
    // Route to the dashboard which has the active-workout flow ready, OR
    // jump straight into the workout page if such a route exists. We use
    // the existing localStorage handoff pattern that TodaysWorkout uses.
    localStorage.setItem("pending_active_workout_plan_id", plan.id);
    navigate("/");
  };

  // Apply a swap: rewrite UserWeeklyWorkoutPlan day-slot to the new plan.
  const swapWorkoutMutation = useMutation({
    mutationFn: async ({ dayKey, newPlanId }) => {
      const cur = await base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: userId });
      const row = cur[0];
      if (!row) throw new Error("No weekly plan exists yet.");
      return base44.entities.UserWeeklyWorkoutPlan.update(row.id, { [dayKey]: newPlanId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userWeeklyWorkoutPlan"] });
      toast({ title: "Workout swapped", description: "Your weekly schedule was updated." });
    },
    onError: (err) => toast({ title: "Swap failed", description: err.message, variant: "destructive" }),
  });

  // Swap one exercise: rewrites the embedded exercises[] on the WorkoutPlan.
  const swapExerciseMutation = useMutation({
    mutationFn: async ({ planId, exerciseIdx, newExercise }) => {
      const plan = workoutsById.get(planId);
      if (!plan) throw new Error("Workout plan not found.");
      const exs = [...(plan.exercises || [])];
      exs[exerciseIdx] = {
        ...exs[exerciseIdx],
        name: newExercise.name,
        // We keep sets/reps/day/rest as-is so the user's prescription
        // doesn't get clobbered when only the movement changes.
      };
      return base44.entities.WorkoutPlan.update(planId, { exercises: exs });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
      toast({ title: "Exercise swapped", description: "Substitute saved." });
    },
    onError: (err) => toast({ title: "Swap failed", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">
            {isCurrentWeek ? "This Week" : isFutureWeek ? "Coming Up" : "Past Week"}
          </h2>
          <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-white/70" data-testid="weekly-range-label">
            <button
              type="button"
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              aria-label="Previous week"
              data-testid="weekly-prev-week"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-medium tabular-nums">{fmtRange(weekStart)}</span>
            <button
              type="button"
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              disabled={isCurrentWeek}
              aria-label="Next week"
              data-testid="weekly-next-week"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/progress")}
          data-testid="weekly-view-progress-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-white/15 bg-white/[0.04] text-sm hover:bg-white/[0.08] hover:border-white/25 transition"
        >
          <TrendingUp className="w-4 h-4" /> View Progress
        </button>
      </div>

      {/* Mon-Sun pill row */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-2 py-3" data-testid="weekly-day-strip">
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map(({ dayKey, dayIdx, date }) => {
            const isTodayDot = isoDate(date) === isoDate(today);
            const hasWorkout = !!weekDays[dayIdx].plan;
            const isWeekend = dayIdx >= 5;
            return (
              <div
                key={dayKey}
                className="flex flex-col items-center gap-1 select-none"
                data-testid={`weekly-day-dot-${dayKey}`}
              >
                <p className={`text-[10px] font-bold tracking-widest ${
                  isTodayDot ? "text-sky-300" : isWeekend ? "text-rose-300/90" : "text-white/45"
                }`}>
                  {DAY_SHORT[dayIdx]}
                </p>
                <div
                  className={`relative w-9 h-9 rounded-full flex items-center justify-center font-display font-bold transition ${
                    isTodayDot
                      ? "bg-sky-500 text-white shadow-[0_0_18px_-2px_rgba(56,189,248,0.85)]"
                      : isWeekend
                        ? "text-rose-300/90"
                        : "text-white"
                  }`}
                >
                  {date.getDate()}
                  {hasWorkout && (
                    <span className={`absolute -bottom-1.5 w-1.5 h-1.5 rounded-full ${isTodayDot ? "bg-white" : "bg-sky-400"}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day cards */}
      <div className="space-y-2.5">
        {weekDays.map(({ dayKey, dayIdx, plan, completedSession }) => (
          <DayCard
            key={dayKey}
            dayKey={dayKey}
            dayIdx={dayIdx}
            plan={plan}
            isToday={isCurrentWeek && dayKey === todayKey}
            isExpanded={expandedDay === dayKey}
            onToggle={() => setExpandedDay((cur) => (cur === dayKey ? null : dayKey))}
            exerciseByName={exerciseByName}
            onStartWorkout={handleStartWorkout}
            onSwapWorkout={(dk) => setSwapWorkoutDayKey(dk)}
            onSwapExercise={(ctx) => setSwapExerciseCtx(ctx)}
            completedSession={completedSession}
            isReadOnly={isReadOnly}
          />
        ))}
      </div>

      {/* Swap dialogs */}
      <SwapWorkoutDialog
        open={!!swapWorkoutDayKey}
        onOpenChange={(o) => !o && setSwapWorkoutDayKey(null)}
        dayKey={swapWorkoutDayKey}
        workouts={workouts}
        currentPlanId={swapWorkoutDayKey ? weeklyWorkoutPlan?.[swapWorkoutDayKey] : null}
        onSelect={(newPlanId) => {
          swapWorkoutMutation.mutate({ dayKey: swapWorkoutDayKey, newPlanId });
          setSwapWorkoutDayKey(null);
        }}
      />
      <SwapExerciseDialog
        open={!!swapExerciseCtx}
        onOpenChange={(o) => !o && setSwapExerciseCtx(null)}
        ctx={swapExerciseCtx}
        allExercises={allExercises}
        onSelect={(newExercise) => {
          if (!swapExerciseCtx) return;
          swapExerciseMutation.mutate({
            planId: swapExerciseCtx.plan.id,
            exerciseIdx: swapExerciseCtx.exerciseIdx,
            newExercise,
          });
          setSwapExerciseCtx(null);
        }}
      />
    </div>
  );
}
